import { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import type { StoreResponse } from '@clarity/contract';
import { useScopeContext } from '../../lib/scopeContext.js';
import { useI18n } from '../../lib/i18n.js';
import { IconInsights } from '../../components/icons.js';
import { DashboardOperations } from '../DashboardOperations.js';
import { splitOnboarded, OnboardingNote } from '../combined/shared.js';
import { BranchInsights } from './BranchInsights.js';

/**
 * Insights (clarity-owner-analytics) — the dedicated Operations/Analytics surface
 * the dashboard glance only hints at. Scope-aware like the other flexible
 * surfaces (clarity-navigation-scope Req 6.4):
 *
 *  - SINGLE branch (store scope, or a one-branch all/brand) → {@link BranchInsights}
 *    with the full detail: ratings, delivery/prep, availability, the conversion
 *    funnel, new-vs-returning, deal incrementality, opening hours, plus the
 *    owner-entry form and the MyThings payouts/costs panel.
 *  - MULTI branch all/brand → the operations ROLL-UP across the in-scope
 *    onboarded branches ({@link DashboardOperations}, reused from the dashboard),
 *    then a chooser to open ONE branch's full detail (incrementality + the funnel
 *    are per-branch, so the deep view is reached one branch at a time — mirroring
 *    Campaigns).
 *
 * The container keeps the stable `data-surface="insights"` + `data-scope` markers
 * the routing tests assert on.
 */
export function Insights() {
  const { scope, branches, store, isSingleBranch } = useScopeContext();
  const { hash } = useLocation();

  const only =
    store ?? (isSingleBranch && branches.length === 1 ? (branches[0] as StoreResponse) : null);

  // A notification deep-link to `#availability` / `#ratings` scrolls that
  // section into view once the branch detail has rendered.
  useEffect(() => {
    if (!hash || !only) return;
    const id = hash.replace('#', '');
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 400);
    return () => clearTimeout(t);
  }, [hash, only]);

  if (only) {
    return (
      <section data-surface="insights" data-scope={scope.kind}>
        <div className="page-head">
          <h2>Insights</h2>
          <p className="sub">{only.name ?? only.externalStoreId} · operations &amp; analytics</p>
        </div>
        <BranchInsights store={only} />
      </section>
    );
  }

  return (
    <section data-surface="insights" data-scope={scope.kind}>
      <InsightsRollup branches={branches} />
    </section>
  );
}

/**
 * The multi-branch roll-up: the operations glance across the in-scope onboarded
 * branches, then a pick-a-branch chooser for the full per-branch detail (ratings
 * breakdown, the conversion funnel, deal incrementality — all per branch).
 */
function InsightsRollup({ branches }: { branches: StoreResponse[] }) {
  const { clientId } = useParams();
  const { t } = useI18n();
  const { onboarded, stillOnboarding } = splitOnboarded(branches);

  return (
    <div className="insights">
      <div className="page-head">
        <h2>Insights</h2>
        <p className="sub">Operations &amp; analytics across your branches.</p>
      </div>

      {stillOnboarding > 0 && <OnboardingNote count={stillOnboarding} t={t} />}

      {/* The same operations roll-up the dashboard shows, reused verbatim. */}
      <DashboardOperations branches={branches} />

      <section className="card insights-block" aria-label="Open a branch">
        <header className="insights-block__head">
          <h3>Full detail per branch</h3>
          <p className="muted">
            The conversion funnel, deal incrementality and the owner-entry form are per branch — open
            one to see it all.
          </p>
        </header>
        {onboarded.length === 0 ? (
          <p className="muted">No onboarded branches in scope yet.</p>
        ) : (
          <ul className="insights__pick" aria-label="Pick a branch">
            {onboarded.map((b) => (
              <li key={b.id}>
                <Link className="card insights__pick-item" to={`/c/${clientId}/store/${b.id}/insights`}>
                  <IconInsights className="nav-ico" aria-hidden="true" />
                  <span>{b.name ?? b.externalStoreId}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
